import { Button, Checkbox, Flex, Form, Input, message, Radio, Space } from 'antd'
import { DistributionFormValues, PromoCodeItem } from 'Modules'
import { useState } from 'react'

import {
  DISTRIBUTION_MESSAGES,
  DISTRIBUTION_SEND_MODE_OPTIONS,
  DISTRIBUTION_SUBJECT_MAX_LENGTH,
  DISTRIBUTION_TYPE_OPTIONS,
  PROMO_CODE_MESSAGES,
} from 'constants/promo-code'
import { useCreateDistribution, useExistingUsersCount } from 'hooks/promo-code'

import { buildDistributionEmailBody } from './email-template'
import { ExistingUsersSelect } from './existing-users-select'
import { ImportFileUpload } from './import-file-upload'

interface SetupTabProps {
  promoCode: PromoCodeItem | null
  canEdit: boolean
  onClose: () => void
}

const INITIAL_VALUES: DistributionFormValues = {
  type: 'ALL_USERS',
  sendEmail: false,
  sendMode: 'ALL_WHITELIST',
  subject: "Mey Real | You've Received an Exclusive Investment Reward",
}

/** Whitelist & Email — Setup tab (one Save & Send Email = one distribution record). */
export const SetupTab = ({ promoCode, canEdit, onClose }: SetupTabProps) => {
  const [form] = Form.useForm<DistributionFormValues>()
  const type = Form.useWatch('type', form) ?? INITIAL_VALUES.type
  const sendEmail = Form.useWatch('sendEmail', form) ?? false
  const userIds = Form.useWatch('userIds', form)
  const importFile = Form.useWatch('importFile', form)
  const subject = Form.useWatch('subject', form)
  const [previewOpen, setPreviewOpen] = useState(false)

  const { total: whitelistTotal } = useExistingUsersCount(
    type === 'ALL_USERS' || type === 'REFERRED_USERS',
    type === 'REFERRED_USERS'
  )

  const createMutation = useCreateDistribution(promoCode?.id)

  // Emails can only go out for an Active code (whitelist itself can still be saved).
  const isActive = promoCode?.status === 'ACTIVE'
  // An Expired code can't be distributed at all — Save & Send Email is locked.
  const isExpired = promoCode?.status === 'EXPIRED'

  // Fixed marketing template — the admin only edits the Subject.
  const emailBody = buildDistributionEmailBody(promoCode?.code ?? '')

  // Per spec the button only enables once every required field is filled.
  const hasWhitelist =
    type === 'EXISTING_USERS'
      ? !!userIds?.length
      : type === 'IMPORT_FILE'
        ? !!importFile?.emails.length
        : true
  const emailReady = !sendEmail || !!subject?.trim()
  // An imported email list is only useful when it is actually emailed, so the
  // Import file option requires "Send email to whitelist users" to be checked.
  const importRequiresEmail = type !== 'IMPORT_FILE' || sendEmail
  const canSave =
    canEdit &&
    !isExpired &&
    hasWhitelist &&
    importRequiresEmail &&
    emailReady &&
    !createMutation.isPending

  const handleSubmit = async () => {
    const values = await form.validateFields()
    createMutation.mutate(
      { ...values, body: values.sendEmail ? emailBody : undefined },
      {
        onSuccess: () => {
          message.success(DISTRIBUTION_MESSAGES.SAVE_SUCCESS)
          onClose()
        },
        onError: (error: any) => {
          message.error(
            error?.response?.data?.message || error?.message || 'Failed to save whitelist'
          )
        },
      }
    )
  }

  return (
    <Form<DistributionFormValues>
      form={form}
      layout="vertical"
      initialValues={INITIAL_VALUES}
      disabled={!canEdit}
      className="whitelist-email__setup"
    >
      <Form.Item name="type" label="Whitelist Users">
        <Radio.Group options={DISTRIBUTION_TYPE_OPTIONS} />
      </Form.Item>

      {(type === 'ALL_USERS' || type === 'REFERRED_USERS') && (
        <div className="whitelist-email__total-users">
          Total users in whitelist: <strong>{whitelistTotal ?? '…'}</strong>
        </div>
      )}

      {type === 'EXISTING_USERS' && (
        <Form.Item
          name="userIds"
          rules={[{ required: true, message: PROMO_CODE_MESSAGES.REQUIRED('Whitelist users') }]}
        >
          <ExistingUsersSelect disabled={!canEdit} />
        </Form.Item>
      )}

      {type === 'IMPORT_FILE' && (
        <Form.Item
          name="importFile"
          rules={[
            {
              validator: (_, value) =>
                value?.emails?.length
                  ? Promise.resolve()
                  : Promise.reject(new Error(PROMO_CODE_MESSAGES.REQUIRED('Whitelist file'))),
            },
          ]}
        >
          <ImportFileUpload disabled={!canEdit} />
        </Form.Item>
      )}

      <Form.Item name="sendEmail" valuePropName="checked" className="whitelist-email__send-email">
        <Checkbox disabled={!canEdit || !isActive}>Send email to whitelist users</Checkbox>
      </Form.Item>
      {!isActive && (
        <div className="whitelist-email__send-email-hint">
          Email can only be sent when the promo code is Active.
        </div>
      )}
      {isActive && type === 'IMPORT_FILE' && !sendEmail && (
        <div className="whitelist-email__send-email-hint">
          Import file requires sending email to the whitelist users.
        </div>
      )}

      {sendEmail && (
        <>
          <Form.Item name="sendMode" label="Send to">
            <Radio.Group>
              <Space direction="vertical">
                {DISTRIBUTION_SEND_MODE_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Subject"
            rules={[
              {
                required: true,
                whitespace: true,
                message: PROMO_CODE_MESSAGES.REQUIRED('Subject'),
              },
            ]}
          >
            <Input
              placeholder="Enter email subject"
              maxLength={DISTRIBUTION_SUBJECT_MAX_LENGTH}
              showCount
            />
          </Form.Item>

          <Form.Item label="Body">
            <div className="whitelist-email__body-note">
              The email body uses the standard promo template with this code filled in.{' '}
              <Button
                type="link"
                size="small"
                disabled={false}
                onClick={() => setPreviewOpen((prev) => !prev)}
              >
                {previewOpen ? 'Hide preview' : 'Preview'}
              </Button>
            </div>
            {previewOpen && (
              <iframe
                title="Email body preview"
                srcDoc={emailBody}
                sandbox=""
                className="whitelist-email__body-preview"
              />
            )}
          </Form.Item>
        </>
      )}

      <Flex justify="flex-end" align="center" gap={12} className="whitelist-email__footer">
        {isExpired && (
          <span className="whitelist-email__send-email-hint">
            This promo code has expired — the whitelist can no longer be updated.
          </span>
        )}
        {/* Keep Cancel clickable even without edit permission (Form disables children). */}
        <Button onClick={onClose} disabled={false}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!canSave}
          loading={createMutation.isPending}
        >
          Save &amp; Send Email
        </Button>
      </Flex>
    </Form>
  )
}
